import React, { useState, useCallback } from 'react';

export interface ImageGalleryImage {
  src: string;
  alt?: string;
}

interface ImageGalleryProps {
  images: ImageGalleryImage[];
  className?: string;
  aspectRatio?: string; // e.g. '16/9', '4/3'
  rounded?: boolean;
  showArrowsOnHover?: boolean;
  enableLoop?: boolean;
  onImageClick?: (image: ImageGalleryImage, index: number) => void;
}

// Lightweight gallery without dot indicators; optimized for potentially many images (>30)
const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  className = '',
  aspectRatio = '16/9',
  rounded = true,
  showArrowsOnHover = true,
  enableLoop = true,
  onImageClick,
}) => {
  const [index, setIndex] = useState(0);
  const count = images.length;

  const next = useCallback(() => {
    setIndex(prev => (prev === count - 1 ? (enableLoop ? 0 : prev) : prev + 1));
  }, [count, enableLoop]);

  const prev = useCallback(() => {
    setIndex(prev => (prev === 0 ? (enableLoop ? count - 1 : prev) : prev - 1));
  }, [count, enableLoop]);

  if (!images || images.length === 0) {
    return (
      <div
        className={`bg-gray-100 flex items-center justify-center text-xs text-gray-500 ${rounded ? 'rounded-lg' : ''}`}
        style={{ aspectRatio: aspectRatio.replace('/', ' / ') }}
      >
        No images
      </div>
    );
  }

  return (
    <div
      className={`relative w-full overflow-hidden group select-none ${rounded ? 'rounded-lg' : ''} ${className}`}
      style={{ aspectRatio: aspectRatio.replace('/', ' / ') }}
    >
      <div
        className={`flex transition-transform duration-500 ease-in-out`}
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {images.map((img, i) => (
          <div key={i} className={`min-w-full h-full bg-gray-50 relative`}>
            <img
              src={img.src}
              alt={img.alt || `image-${i + 1}`}
              loading="lazy"
              draggable={false}
              className="w-full h-full object-cover"
              onClick={() => onImageClick?.(img, i)}
            />
          </div>
        ))}
      </div>
      {count > 1 && (
        <>
          <button
            aria-label="Previous image"
            onClick={prev}
            className={`absolute top-1/2 left-3 -translate-y-1/2 h-9 w-9 flex items-center justify-center rounded-full shadow-sm backdrop-blur-sm transition-opacity
              ${showArrowsOnHover ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}
              bg-white/70 hover:bg-white/90 text-gray-800 border border-white/60`}
          >
            <span className="text-xl leading-none">&#10094;</span>
          </button>
          <button
            aria-label="Next image"
            onClick={next}
            className={`absolute top-1/2 right-3 -translate-y-1/2 h-9 w-9 flex items-center justify-center rounded-full shadow-sm backdrop-blur-sm transition-opacity
              ${showArrowsOnHover ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}
              bg-white/70 hover:bg-white/90 text-gray-800 border border-white/60`}
          >
            <span className="text-xl leading-none">&#10095;</span>
          </button>
          <div className="absolute bottom-2 right-3 bg-black/40 text-white text-[10px] px-2 py-1 rounded-full tracking-wide">
            {index + 1}/{count}
          </div>
        </>
      )}
    </div>
  );
};

export default ImageGallery;
